/**
 * Deployment / hosting write surface (`/deployment`).
 * Counterpart of the frontend hostingService and CLI ``DeploymentClient``.
 * All routes require a Bearer session token (or API key accepted as Bearer).
 */

import { BackendClient } from './BackendClient';

export class DeploymentClient extends BackendClient {
  constructor(apiKey?: string, webBackendUrl?: string) {
    super(apiKey, webBackendUrl);
    this.addEndpoint('deployment/analyze', 'POST', 'required');
    this.addEndpoint('deployment/draft', 'POST', 'required');
    this.addEndpoint('deployment/hf_token', 'POST', 'required');
    this.addEndpoint('deployment/upsert_ai_service', 'POST', 'required');
    this.addEndpoint('deployment/upsert_service_endpoint', 'POST', 'required');
    this.addEndpoint('deployment/upsert_deployment', 'POST', 'required');
    this.addEndpoint('deployment/upsert_service', 'POST', 'required');
    this.addEndpoint('deployment/is_dockerhub_repo_accessible', 'POST', 'required');
    this.addEndpoint('deployment/is_service_name_available', 'GET', 'required');
    this.addEndpoint('deployment/order_async_deployment', 'POST', 'required');
    this.addEndpoint('deployment/deployment_status', 'GET', 'required');
    this.addEndpoint('deployment/delete_service', 'POST', 'required');
    this.addEndpoint('deployment/private_slots', 'GET', 'required');
  }

  async analyze(apipodConfig: Record<string, any>): Promise<Record<string, any>> {
    return this.postJson('deployment/analyze', apipodConfig);
  }

  async createDraft(
    apipodConfig: Record<string, any>,
    modelResolutions: Record<string, string | null> = {},
  ): Promise<Record<string, any>> {
    return this.postJson('deployment/draft', {
      apipod_config: apipodConfig,
      model_resolutions: modelResolutions,
    });
  }

  async upsertHfToken(token: string, alias = 'default'): Promise<Record<string, any>> {
    return this.postJson('deployment/hf_token', undefined, { token, alias });
  }

  async listHfTokens(): Promise<Record<string, any>[]> {
    this.addEndpoint('deployment/hf_token', 'GET', 'required');
    const raw = await this.getJson('deployment/hf_token');
    return Array.isArray(raw) ? raw : [];
  }

  async upsertAiService(service: Record<string, any>): Promise<Record<string, any>> {
    return this.postJson('deployment/upsert_ai_service', service);
  }

  async upsertServiceEndpoint(endpoint: Record<string, any>): Promise<Record<string, any>> {
    return this.postJson('deployment/upsert_service_endpoint', endpoint);
  }

  async upsertDeployment(
    deployment: Record<string, any>,
    service: Record<string, any> | null = null,
  ): Promise<Record<string, any>> {
    return this.postJson('deployment/upsert_deployment', { deployment, service });
  }

  async upsertService(
    service: Record<string, any>,
    deployment: Record<string, any>,
  ): Promise<Record<string, any>> {
    return this.postJson('deployment/upsert_service', { service, deployment });
  }

  async orderAsyncDeployment(
    service: Record<string, any>,
    deployment: Record<string, any>,
  ): Promise<Record<string, any>> {
    return this.postJson('deployment/order_async_deployment', { service, deployment });
  }

  async getDeploymentStatus(
    jobId: string,
    serviceId: string,
    deploymentId: string,
    context: Record<string, any> = {},
  ): Promise<Record<string, any>> {
    return this.getJson('deployment/deployment_status', {
      job_id: jobId,
      service_id: serviceId,
      deployment_id: deploymentId,
      ...context,
    });
  }

  async isDockerhubRepoAccessible(
    namespace: string,
    repo: string,
    tag: string,
    patId?: string | null,
  ): Promise<boolean | { accessible: boolean; detail?: string | null }> {
    return this.postJson('deployment/is_dockerhub_repo_accessible', undefined, {
      namespace,
      repo,
      tag,
      pat_id: patId ?? undefined,
    });
  }

  async isServiceNameAvailable(serviceName: string): Promise<boolean> {
    const result = await this.getJson('deployment/is_service_name_available', {
      service_name: serviceName,
    });
    return result === true;
  }

  async deleteService(serviceId: string): Promise<Record<string, any>> {
    return this.postJson('deployment/delete_service', { service_id: serviceId });
  }

  async getPrivateSlots(): Promise<{ plan: string; used: number; limit: number }> {
    return this.getJson('deployment/private_slots');
  }

  async recordValidationResult(
    serviceId: string,
    status: 'passed' | 'failed' | 'pending',
  ): Promise<Record<string, any>> {
    this.addEndpoint(`deployment/services/${serviceId}/validation-result`, 'POST', 'required');
    return this.postJson(`deployment/services/${serviceId}/validation-result`, { status });
  }

  async getPublishReadiness(serviceId: string): Promise<Record<string, any>> {
    this.addEndpoint(`deployment/services/${serviceId}/publish-readiness`, 'GET', 'required');
    return this.getJson(`deployment/services/${serviceId}/publish-readiness`);
  }

  async publishService(serviceId: string): Promise<Record<string, any>> {
    this.addEndpoint(`deployment/services/${serviceId}/publish`, 'POST', 'required');
    return this.postJson(`deployment/services/${serviceId}/publish`, {});
  }

  async unpublishService(serviceId: string): Promise<Record<string, any>> {
    this.addEndpoint(`deployment/services/${serviceId}/unpublish`, 'POST', 'required');
    return this.postJson(`deployment/services/${serviceId}/unpublish`, {});
  }
}
